import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useOutletContext, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Dropdown, DropdownOption, Icon, formatNumber } from '@column/column-ui-kit';

import { RenderFields, Section } from '~/components';
import { LoanRepository } from '~/repositories';
import { BankAccountRepository } from '~/repositories/BankAccountRepository';
import { CardAccountRepository } from '~/repositories/CardAccountRepository';
import { useNotificationStore } from '~/stores/Notification';
import { Flex, Inner } from '~/styles';

import { CardProgramProps } from './CardProgram';

interface Params {
  id: string;
}

interface CreateCardAccountForm {
  bankAccountId: string;
  loanId: string;
}

const ActionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-radius: 8px;
  background: ${({ theme }) => theme.secondary.blendToBackground(50)};
  border: 1px solid ${({ theme }) => theme.secondary.blendToBackground(100)};
`;

const ActionIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${({ theme }) => theme.secondary.blendToBackground(100)};

  svg {
    --icon-size: 20px;
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(700)};
  }
`;

const ActionTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.foreground};
`;

const ActionDescription = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.secondary.blendToBackground(700)};
`;

export const PageCardProgramActions: React.FC = () => {
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { id } = useParams<keyof Params>() as Params;
  const { cardProgram } = useOutletContext<CardProgramProps>();

  const isDebit = cardProgram?.cardProgramType === 'debit';
  const isCreditOrCharge = cardProgram?.cardProgramType === 'credit' || cardProgram?.cardProgramType === 'charge';

  const [bankAccountOptions, setBankAccountOptions] = useState<DropdownOption[]>([]);
  const [loanOptions, setLoanOptions] = useState<DropdownOption[]>([]);

  const formMethods = useForm<CreateCardAccountForm>({
    defaultValues: {
      bankAccountId: '',
      loanId: '',
    },
  });

  const { handleSubmit, reset } = formMethods;

  const fetchBankAccounts = useCallback(
    async (search?: string) => {
      try {
        const response = await BankAccountRepository.getAll({ description: search });
        const entries: DropdownOption[] = (response.bankAccounts ?? []).map((entry: any) => ({
          label: `${
            entry?.displayName && entry?.description
              ? `${entry.displayName} – ${entry.description}`
              : entry?.description || 'Unnamed'
          } (${formatNumber(entry?.balances?.availableAmount)})`,
          small: entry.id,
          value: entry.id,
        }));
        setBankAccountOptions(entries);
      } catch (error) {
        addDangerNotification({ content: (error as any)?.message || 'Failed to fetch bank accounts' });
      }
    },
    [addDangerNotification]
  );

  const fetchLoans = useCallback(async () => {
    try {
      const response = await LoanRepository.getAll();
      const entries: DropdownOption[] = (response.loans ?? []).map((entry) => ({
        label: entry.description || 'Unnamed',
        small: entry.id,
        value: entry.id,
      }));
      setLoanOptions(entries);
    } catch (error) {
      addDangerNotification({ content: (error as any)?.message || 'Failed to fetch loans' });
    }
  }, [addDangerNotification]);

  useEffect(() => {
    if (isDebit) {
      fetchBankAccounts();
    }
    if (isCreditOrCharge) {
      fetchLoans();
    }
  }, [isDebit, isCreditOrCharge, fetchBankAccounts, fetchLoans]);

  const createAccountMutation = useMutation({
    mutationFn: (data: CreateCardAccountForm) => {
      if (!id) throw new Error('Card program ID is required');
      return CardAccountRepository.create(id, {
        ...(data.bankAccountId ? { bankAccountId: data.bankAccountId } : {}),
        ...(data.loanId ? { loanId: data.loanId } : {}),
      });
    },
    onSuccess: () => {
      addSuccessNotification({
        content: 'Card account created successfully',
      });
      reset();
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error?.message || 'Failed to create card account',
      });
    },
  });

  if (!cardProgram) {
    return null;
  }

  const onSubmit = (data: CreateCardAccountForm) => {
    createAccountMutation.mutate(data);
  };

  const debitFields = [
    {
      id: 'bankAccountId' as const,
      label: 'Bank Account',
      description: 'Select a bank account to fund this card account',
      children: ({ value, onChange }: { value: any; onChange: (v: any) => void }) => (
        <Dropdown
          options={bankAccountOptions}
          active={value}
          fullWidth
          maxWidth="640px"
          search
          searchLabel="Search by description"
          onSearchChange={(search: string) => fetchBankAccounts(search)}
          onChange={onChange}
        />
      ),
    },
  ];

  const creditChargeFields = [
    {
      id: 'loanId' as const,
      label: 'Loan',
      description: 'Select a loan for this card account',
      rules: { required: true },
      children: ({ value, onChange }: { value: any; onChange: (v: any) => void }) => (
        <Dropdown
          options={loanOptions}
          active={value}
          fullWidth
          maxWidth="640px"
          search
          searchLabel="Search by description"
          onChange={onChange}
        />
      ),
    },
  ];

  const fields = isDebit ? debitFields : isCreditOrCharge ? creditChargeFields : [];

  return (
    <Inner p={0}>
      <Section headline="Create Account">
        <FormProvider {...formMethods}>
          <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
            <Inner px="var(--page-margin)" py={16}>
              <Flex flexDirection="column" gap={12}>
                <ActionCard>
                  <Flex alignItems="center" gap={12}>
                    <ActionIconWrapper>
                      <Icon.CircleAdd />
                    </ActionIconWrapper>
                    <Flex flexDirection="column" gap={2}>
                      <ActionTitle>Create Account</ActionTitle>
                      <ActionDescription>Create a new card account under this program.</ActionDescription>
                    </Flex>
                  </Flex>
                  {RenderFields<CreateCardAccountForm>({
                    sections: [
                      {
                        fields,
                      },
                    ],
                  })}
                  <Flex justifyContent="flex-end">
                    <Button
                      variant="primary"
                      icon={<Icon.CircleAdd />}
                      onClick={handleSubmit(onSubmit)}
                      isLoading={createAccountMutation.isPending}
                    >
                      Create Account
                    </Button>
                  </Flex>
                </ActionCard>
              </Flex>
            </Inner>
          </form>
        </FormProvider>
      </Section>
    </Inner>
  );
};
